import React, { useState, useMemo } from 'react';
import { toLocalDateStr } from '../../utils/formatters';

const DEFAULT_PRESETS = [
  { label: 'MTD',          days: 'mtd' },
  { label: 'Last Month',   days: 'last_month' },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
  { label: 'YTD',          days: 'ytd' },
  { label: '6 Months',     days: 180 },
  { label: '12 Months',    days: 365 },
];

const toDateStr = toLocalDateStr;

function getPresetRange(opt) {
  const now = new Date();
  let start, end;
  if (opt === 'mtd') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = now;
  } else if (opt === 'last_month') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end   = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prev month
  } else if (opt === 'ytd') {
    start = new Date(now.getFullYear(), 0, 1);
    end = now;
  } else {
    start = new Date();
    start.setDate(start.getDate() - opt);
    end = now;
  }
  return { start: toDateStr(start), end: toDateStr(end) };
}

export default function DateRangePicker({ startDate, endDate, onChange, presets, defaultPresetIdx = 2, showCustom = true }) {
  const activePresets = presets || DEFAULT_PRESETS;
  const [presetIdx, setPresetIdx] = useState(defaultPresetIdx);
  const [useCustom, setUseCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const { start, end } = useMemo(() => {
    if (useCustom && customStart && customEnd) return { start: customStart, end: customEnd };
    return getPresetRange(activePresets[presetIdx]?.days || 90);
  }, [presetIdx, useCustom, customStart, customEnd, activePresets]);

  // Notify parent of changes
  React.useEffect(() => {
    if (onChange) onChange({ start, end });
  }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={styles.row}>
      {activePresets.map((opt, i) => (
        <button key={opt.label} onClick={() => { setPresetIdx(i); setUseCustom(false); }}
          style={{ ...styles.btn, ...(!useCustom && presetIdx === i ? styles.btnActive : {}) }}>
          {opt.label}
        </button>
      ))}
      {showCustom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginLeft: '0.5rem', borderLeft: '1px solid #e2e8f0', paddingLeft: '0.75rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Custom:</span>
          <input type="date" value={useCustom ? customStart : start} onChange={e => { setCustomStart(e.target.value); setUseCustom(true); }} style={styles.dateInput} />
          <span style={{ color: '#94a3b8' }}>—</span>
          <input type="date" value={useCustom ? customEnd : end} onChange={e => { setCustomEnd(e.target.value); setUseCustom(true); }} style={styles.dateInput} />
        </div>
      )}
      <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8' }}>{start} to {end}</span>
    </div>
  );
}

const styles = {
  row: {
    display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
    background: '#fff', padding: '0.75rem 1rem', borderRadius: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  btn: {
    padding: '0.4rem 0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600',
    color: '#475569', transition: 'all 0.15s',
  },
  btnActive: { background: '#6366f1', color: '#fff', border: '1px solid #6366f1' },
  dateInput: {
    padding: '0.3rem 0.5rem', border: '1px solid #e2e8f0', borderRadius: '0.375rem',
    fontSize: '0.75rem', color: '#1e293b', outline: 'none', background: '#f8fafc',
  },
};
